import React, { useState, useCallback, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { ProtectedRoute } from '../core/router/guards/ProtectedRoute';
import { AppHeader } from './AppHeader';
import { AppSidebar } from './AppSidebar';
import { PageBackground, BackToTop, useBackToTop } from '@tasama/ui';
import { useServiceTitlesListQuery } from '@/hooks/useServiceTitlesListQuery';
import { useAnalyticsIdentity } from '../hooks/use-analytics-identity.hook';

interface AppLayoutProps {
  testID?: string | undefined;
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  testID = 'app-layout',
}) => {
  // Prefetch the sysId → name catalogue as soon as the authenticated shell
  // mounts — React Query caches it so child routes get it instantly.
  useServiceTitlesListQuery();
  useAnalyticsIdentity();

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const handleMenuToggle = useCallback(() => {
    setIsMobileSidebarOpen((prev) => !prev);
  }, []);

  const handleMobileClose = useCallback(() => {
    setIsMobileSidebarOpen(false);
  }, []);

  // Every routed page scrolls within this container (the layout body itself
  // has no window scroll), so "back to top" watches and scrolls it directly
  // rather than window — see TDF-1386.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { isVisible: isBackToTopVisible, scrollToTop } = useBackToTop({
    containerRef: scrollContainerRef,
  });

  return (
    <ProtectedRoute testID={`${testID}-protection`}>
      <PageBackground>
        {/* Portal-owned layout chain: this wrapper is `flex flex-col h-full`
            so the inner `flex-1` body actually fills the viewport below the
            header. Scoped here (not inside PageBackground) because
            PageBackground is a generic atom and shouldn't impose a flex
            layout on its other consumers. */}
        <div className="flex flex-col h-full">
          <AppHeader
            testID={`${testID}-header`}
            onMenuToggle={handleMenuToggle}
          />
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
            <div className="max-w-container-3xl mx-auto h-full">
              <div
                className="flex gap-0 lg:gap-3xl min-h-full p-4 pb-95 md:p-6 md:pb-95 lg:p-3xl"
                data-testid={testID}
              >
                <AppSidebar
                  testID={`${testID}-sidebar`}
                  isMobileOpen={isMobileSidebarOpen}
                  onMobileClose={handleMobileClose}
                />
                <div className="flex flex-col flex-1 min-w-0">
                  <main
                    className="flex-1 flex flex-col"
                    data-testid={`${testID}-main-content`}
                  >
                    <Outlet />
                  </main>
                </div>
              </div>
            </div>
          </div>
          <BackToTop
            visible={isBackToTopVisible}
            onClick={scrollToTop}
            testID={`${testID}-back-to-top`}
          />
        </div>
      </PageBackground>
    </ProtectedRoute>
  );
};
